import type {
  DetectedRecoveryInterval,
  DetectedWorkInterval,
  IntervalClassification,
  Ride,
  RideRecord,
  WorkoutDetectedSet
} from "../types/ride";

const FEET_PER_METER = 3.28084;
const MPH_PER_MPS = 2.2369362921;
const MILES_PER_METER = 0.0006213711922;

export type RideChartPoint = {
  index: number;
  elapsedSeconds: number;
  clockTime: string | null;
  powerWatts: number | null;
  power30sWatts: number | null;
  heartRateBpm: number | null;
  cadenceRpm: number | null;
  elevationFeet: number | null;
  speedMph: number | null;
  distanceMiles: number | null;
  minuteVentilation: number | null;
  breathingFrequency: number | null;
};

export type RespiratoryChartPoint = {
  elapsedSeconds: number;
  minuteVentilation: number | null;
  breathingFrequency: number | null;
};

export type RideChartIntervalOverlayKind = "primary-work" | "supporting-endurance" | "supporting-work" | "recovery";

export type RideChartIntervalOverlay = {
  id: string;
  intervalId: string | null;
  classification: IntervalClassification | null;
  kind: RideChartIntervalOverlayKind;
  startElapsedSeconds: number;
  endElapsedSeconds: number;
  label: string;
};

export type RideAnalysisChartModel = {
  points: RideChartPoint[];
  domainStartSeconds: number;
  domainEndSeconds: number;
  hasPower: boolean;
  hasHeartRate: boolean;
  hasCadence: boolean;
  hasElevation: boolean;
  hasGps: boolean;
  hasMinuteVentilation: boolean;
  hasBreathingFrequency: boolean;
  ftpWatts: number | null;
  overlays: RideChartIntervalOverlay[];
  overlaysByIntervalId: Record<string, RideChartIntervalOverlay>;
};

export type RideAnalysisInteractionState = {
  rideKey: string | null;
  selectedIntervalId: string | null;
  viewportStartSeconds: number | null;
  viewportEndSeconds: number | null;
};

type SanitizedPoint = {
  index: number;
  elapsedSeconds: number;
  clockTime: string | null;
  powerWatts: number | null;
  power30sWatts: number | null;
  heartRateBpm: number | null;
  cadenceRpm: number | null;
  elevationFeet: number | null;
  speedMph: number | null;
  distanceMiles: number | null;
  minuteVentilation: number | null;
  breathingFrequency: number | null;
};

type TimingContext = {
  elapsedSeries: number[];
  toCanonicalElapsedSeconds: (value: { timestamp: string | null | undefined; elapsedSeconds: number | null | undefined }) => number | null;
};

function toFiniteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function clampToNonNegative(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function normalizeTimestampToIso(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : new Date(parsed).toISOString();
}

function detectElapsedScale(elapsedValues: number[]): number {
  if (elapsedValues.length === 0) {
    return 1;
  }

  const maxValue = Math.max(...elapsedValues);
  if (maxValue > 100000) {
    return 1000;
  }

  if (elapsedValues.length < 2 || maxValue <= 10000) {
    return 1;
  }

  const sorted = [...elapsedValues].sort((left, right) => left - right);
  const deltas: number[] = [];
  for (let index = 1; index < sorted.length; index += 1) {
    const delta = sorted[index] - sorted[index - 1];
    if (delta > 0 && Number.isFinite(delta)) {
      deltas.push(delta);
    }
  }

  if (deltas.length === 0) {
    return 1;
  }

  const medianDelta = deltas.sort((left, right) => left - right)[Math.floor(deltas.length / 2)];
  return medianDelta > 20 ? 1000 : 1;
}

function createTimingContext(records: RideRecord[]): TimingContext {
  const parsedTimestampMs = records.map(record => {
    if (!record.timestamp) {
      return null;
    }

    const parsed = Date.parse(record.timestamp);
    return Number.isNaN(parsed) ? null : parsed;
  });

  const finiteElapsedValues = records
    .map(record => toFiniteNumber(record.elapsedSeconds))
    .filter((value): value is number => value !== null);
  const elapsedScale = detectElapsedScale(finiteElapsedValues);

  const firstTimestampMs = parsedTimestampMs.find(value => value !== null) ?? null;
  const lastTimestampMs = [...parsedTimestampMs].reverse().find(value => value !== null) ?? null;
  const hasValidTimestampDomain =
    firstTimestampMs !== null &&
    lastTimestampMs !== null &&
    Number.isFinite(firstTimestampMs) &&
    Number.isFinite(lastTimestampMs) &&
    lastTimestampMs > firstTimestampMs;

  const firstElapsedRaw = records
    .map(record => toFiniteNumber(record.elapsedSeconds))
    .find(value => value !== null) ?? null;
  const firstElapsedSeconds = firstElapsedRaw === null ? null : Math.max(0, firstElapsedRaw / elapsedScale);

  const toCanonicalElapsedSeconds = ({
    timestamp,
    elapsedSeconds
  }: {
    timestamp: string | null | undefined;
    elapsedSeconds: number | null | undefined;
  }): number | null => {
    if (hasValidTimestampDomain && timestamp) {
      const parsed = Date.parse(timestamp);
      if (!Number.isNaN(parsed) && firstTimestampMs !== null) {
        return Math.max(0, (parsed - firstTimestampMs) / 1000);
      }
    }

    const finiteElapsed = toFiniteNumber(elapsedSeconds);
    if (finiteElapsed !== null) {
      const scaledElapsed = Math.max(0, finiteElapsed / elapsedScale);
      if (firstElapsedSeconds !== null) {
        return Math.max(0, scaledElapsed - firstElapsedSeconds);
      }

      return scaledElapsed;
    }

    return null;
  };

  const elapsedSeries: number[] = [];
  let fallbackElapsed = 0;

  records.forEach(record => {
    const canonical = toCanonicalElapsedSeconds({
      timestamp: record.timestamp,
      elapsedSeconds: record.elapsedSeconds
    });

    if (canonical !== null && Number.isFinite(canonical)) {
      fallbackElapsed = elapsedSeries.length > 0 ? Math.max(fallbackElapsed, canonical) : Math.max(0, canonical);
      elapsedSeries.push(fallbackElapsed);
      return;
    }

    fallbackElapsed = elapsedSeries.length === 0 ? 0 : fallbackElapsed + 1;
    elapsedSeries.push(fallbackElapsed);
  });

  return {
    elapsedSeries,
    toCanonicalElapsedSeconds
  };
}

function computeRollingPower(points: Array<{ elapsedSeconds: number; powerWatts: number | null }>, windowSeconds: number): Array<number | null> {
  const results: Array<number | null> = new Array(points.length).fill(null);
  const queue: Array<{ elapsedSeconds: number; powerWatts: number }> = [];
  let sum = 0;

  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];

    if (point.powerWatts !== null && Number.isFinite(point.powerWatts)) {
      queue.push({ elapsedSeconds: point.elapsedSeconds, powerWatts: point.powerWatts });
      sum += point.powerWatts;
    }

    while (queue.length > 0 && queue[0].elapsedSeconds < point.elapsedSeconds - windowSeconds) {
      const removed = queue.shift();
      if (removed) {
        sum -= removed.powerWatts;
      }
    }

    if (point.powerWatts !== null && queue.length > 0) {
      const nextValue = sum / queue.length;
      results[index] = Number.isFinite(nextValue) ? nextValue : null;
    }
  }

  return results;
}

function sanitizeAndSortPoints(points: RideChartPoint[]): SanitizedPoint[] {
  const filtered = points.filter(point => Number.isFinite(point.elapsedSeconds));

  filtered.sort((left, right) => left.elapsedSeconds - right.elapsedSeconds);

  const deduped: SanitizedPoint[] = [];
  for (const point of filtered) {
    const safePoint: SanitizedPoint = {
      ...point,
      elapsedSeconds: clampToNonNegative(point.elapsedSeconds),
      powerWatts: toFiniteNumber(point.powerWatts),
      power30sWatts: toFiniteNumber(point.power30sWatts),
      heartRateBpm: toFiniteNumber(point.heartRateBpm),
      cadenceRpm: toFiniteNumber(point.cadenceRpm),
      elevationFeet: toFiniteNumber(point.elevationFeet),
      speedMph: toFiniteNumber(point.speedMph),
      distanceMiles: toFiniteNumber(point.distanceMiles),
      minuteVentilation: toFiniteNumber(point.minuteVentilation),
      breathingFrequency: toFiniteNumber(point.breathingFrequency)
    };

    const last = deduped[deduped.length - 1];
    if (last && last.elapsedSeconds === safePoint.elapsedSeconds) {
      deduped[deduped.length - 1] = {
        ...last,
        ...safePoint,
        powerWatts: safePoint.powerWatts ?? last.powerWatts,
        power30sWatts: safePoint.power30sWatts ?? last.power30sWatts,
        heartRateBpm: safePoint.heartRateBpm ?? last.heartRateBpm,
        cadenceRpm: safePoint.cadenceRpm ?? last.cadenceRpm,
        elevationFeet: safePoint.elevationFeet ?? last.elevationFeet,
        speedMph: safePoint.speedMph ?? last.speedMph,
        distanceMiles: safePoint.distanceMiles ?? last.distanceMiles,
        minuteVentilation: safePoint.minuteVentilation ?? last.minuteVentilation,
        breathingFrequency: safePoint.breathingFrequency ?? last.breathingFrequency,
        clockTime: safePoint.clockTime ?? last.clockTime
      };
      continue;
    }

    deduped.push(safePoint);
  }

  return deduped;
}

function sanitizeOverlay(
  overlay: RideChartIntervalOverlay,
  domainStartSeconds: number,
  domainEndSeconds: number
): RideChartIntervalOverlay | null {
  if (!Number.isFinite(overlay.startElapsedSeconds) || !Number.isFinite(overlay.endElapsedSeconds)) {
    return null;
  }

  const clampedStart = Math.max(domainStartSeconds, overlay.startElapsedSeconds);
  const clampedEnd = Math.min(domainEndSeconds, overlay.endElapsedSeconds);

  if (!Number.isFinite(clampedStart) || !Number.isFinite(clampedEnd) || clampedEnd <= clampedStart) {
    return null;
  }

  return {
    ...overlay,
    startElapsedSeconds: clampedStart,
    endElapsedSeconds: clampedEnd
  };
}

export function getSafePlotDimension(value: number | null | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return 1;
  }

  return Math.max(1, Math.round(value));
}

function toCadenceValue(value: number | null | undefined): number | null {
  const numericValue = toFiniteNumber(value);
  if (numericValue === null) {
    return null;
  }

  return numericValue;
}

function toOverlayLabel(classification: IntervalClassification | null, kind: RideChartIntervalOverlayKind): string {
  const base = classification ? classification.replace("-", " ") : "recovery";
  if (kind === "primary-work") {
    return `Primary ${base}`;
  }

  if (kind === "supporting-endurance") {
    return `Supporting endurance`;
  }

  if (kind === "supporting-work") {
    return `Supporting ${base}`;
  }

  return "Recovery";
}

function createPrimarySetMemberIndex(sets: WorkoutDetectedSet[] | undefined): Set<string> {
  const primarySet = sets?.find(set => set.isPrimarySet);
  return new Set(primarySet?.members ?? []);
}

function buildRecoveryOverlay(
  recovery: DetectedRecoveryInterval,
  recoveryIndex: number,
  intervalsById: Map<string, DetectedWorkInterval>,
  sortedIntervals: DetectedWorkInterval[]
): RideChartIntervalOverlay | null {
  const parentInterval = intervalsById.get(recovery.associatedIntervalId);
  if (!parentInterval) {
    return null;
  }

  const startElapsedSeconds = parentInterval.endElapsedSeconds;
  const parentIntervalIndex = sortedIntervals.findIndex(interval => interval.id === parentInterval.id);
  const nextInterval = parentIntervalIndex >= 0 ? sortedIntervals[parentIntervalIndex + 1] : undefined;
  const durationSeconds = Math.max(0, toFiniteNumber(recovery.durationSeconds) ?? 0);

  let endElapsedSeconds = startElapsedSeconds + durationSeconds;
  if (nextInterval) {
    endElapsedSeconds = Math.min(endElapsedSeconds, nextInterval.startElapsedSeconds);
  }

  if (endElapsedSeconds <= startElapsedSeconds) {
    return null;
  }

  return {
    id: `recovery-${recovery.associatedIntervalId}-${recoveryIndex + 1}`,
    intervalId: recovery.associatedIntervalId,
    classification: null,
    kind: "recovery",
    startElapsedSeconds,
    endElapsedSeconds,
    label: "Recovery"
  };
}

export function buildRideAnalysisChartModel(ride: Ride, ftpWatts: number | null | undefined, respiratoryPoints: RespiratoryChartPoint[] = []): RideAnalysisChartModel {
  const sourceRecords = Array.isArray(ride.records) ? ride.records : [];
  const timing = createTimingContext(sourceRecords);
  const elapsedSeries = timing.elapsedSeries;

  const basePoints: Array<{ elapsedSeconds: number; powerWatts: number | null }> = sourceRecords.map((record, index) => ({
    elapsedSeconds: elapsedSeries[index],
    powerWatts: toFiniteNumber(record.powerWatts)
  }));

  const smoothedPower = computeRollingPower(basePoints, 30);

  const nearestRespiratory = (elapsedSeconds: number): RespiratoryChartPoint | null => {
    if (!respiratoryPoints.length) return null;
    let nearest = respiratoryPoints[0];
    for (const point of respiratoryPoints) {
      if (Math.abs(point.elapsedSeconds - elapsedSeconds) < Math.abs(nearest.elapsedSeconds - elapsedSeconds)) nearest = point;
    }
    return Math.abs(nearest.elapsedSeconds - elapsedSeconds) <= 60 ? nearest : null;
  };
  const rawPoints: RideChartPoint[] = sourceRecords.map((record, index) => {
    const respiratory = nearestRespiratory(elapsedSeries[index]);
    return ({
    index,
    elapsedSeconds: elapsedSeries[index],
    clockTime: normalizeTimestampToIso(record.timestamp),
    powerWatts: toFiniteNumber(record.powerWatts),
    power30sWatts: smoothedPower[index],
    heartRateBpm: toFiniteNumber(record.heartRateBpm),
    cadenceRpm: toCadenceValue(record.cadenceRpm),
    elevationFeet: toFiniteNumber(record.altitudeMeters) === null ? null : (toFiniteNumber(record.altitudeMeters) as number) * FEET_PER_METER,
    speedMph: toFiniteNumber(record.speedMetersPerSecond) === null ? null : (toFiniteNumber(record.speedMetersPerSecond) as number) * MPH_PER_MPS,
    distanceMiles: toFiniteNumber(record.distanceMeters) === null ? null : (toFiniteNumber(record.distanceMeters) as number) * MILES_PER_METER,
    minuteVentilation: respiratory?.minuteVentilation ?? null,
    breathingFrequency: respiratory?.breathingFrequency ?? null
  });
  });

  const points = sanitizeAndSortPoints(rawPoints);

  const domainStartSeconds = points.length === 0 ? 0 : points[0].elapsedSeconds;
  const rawDomainEndSeconds = points.length === 0 ? 0 : points[points.length - 1].elapsedSeconds;
  const domainEndSeconds = rawDomainEndSeconds > domainStartSeconds ? rawDomainEndSeconds : domainStartSeconds + 1;

  const intervalDetection = ride.rideAnalytics?.intervalDetection;
  const workoutClassification = ride.rideAnalytics?.workoutClassification;
  const primarySetMembers = createPrimarySetMemberIndex(workoutClassification?.detectedSets);
  const overlays: RideChartIntervalOverlay[] = [];
  const overlaysByIntervalId: Record<string, RideChartIntervalOverlay> = {};

  const detectedIntervals = intervalDetection?.detectedIntervals ?? [];
  const sortedIntervals = [...detectedIntervals].sort((left, right) => left.startElapsedSeconds - right.startElapsedSeconds);
  const intervalIndex = new Map(sortedIntervals.map(interval => [interval.id, interval]));

  for (const interval of sortedIntervals) {
    if (!Number.isFinite(interval.startElapsedSeconds) || !Number.isFinite(interval.endElapsedSeconds)) {
      continue;
    }

    const isPrimary = primarySetMembers.has(interval.id);
    const isSupportingEndurance = !isPrimary && interval.classification === "endurance";
    const kind: RideChartIntervalOverlayKind = isPrimary
      ? "primary-work"
      : isSupportingEndurance
        ? "supporting-endurance"
        : "supporting-work";

    const overlay: RideChartIntervalOverlay = {
      id: interval.id,
      intervalId: interval.id,
      classification: interval.classification,
      kind,
      startElapsedSeconds:
        timing.toCanonicalElapsedSeconds({ timestamp: interval.startTimestamp, elapsedSeconds: interval.startElapsedSeconds }) ??
        interval.startElapsedSeconds,
      endElapsedSeconds:
        timing.toCanonicalElapsedSeconds({ timestamp: interval.endTimestamp, elapsedSeconds: interval.endElapsedSeconds }) ??
        interval.endElapsedSeconds,
      label: toOverlayLabel(interval.classification, kind)
    };

    const sanitized = sanitizeOverlay(overlay, domainStartSeconds, domainEndSeconds);
    if (!sanitized) {
      continue;
    }

    overlays.push(sanitized);
    overlaysByIntervalId[interval.id] = sanitized;
  }

  for (const [index, recovery] of (intervalDetection?.detectedRecoveries ?? []).entries()) {
    const overlay = buildRecoveryOverlay(recovery, index, intervalIndex, sortedIntervals);
    if (overlay) {
      const sanitized = sanitizeOverlay(overlay, domainStartSeconds, domainEndSeconds);
      if (sanitized) {
        overlays.push(sanitized);
      }
    }
  }

  overlays.sort((left, right) => left.startElapsedSeconds - right.startElapsedSeconds);

  const hasPower = points.some(point => point.powerWatts !== null);
  const hasHeartRate = points.some(point => point.heartRateBpm !== null);
  const hasCadence = points.some(point => point.cadenceRpm !== null);
  const hasElevation = points.some(point => point.elevationFeet !== null);
  const hasGps = points.some(point => point.distanceMiles !== null || point.speedMph !== null);
  const hasMinuteVentilation = points.some(point => point.minuteVentilation !== null);
  const hasBreathingFrequency = points.some(point => point.breathingFrequency !== null);

  return {
    points,
    domainStartSeconds,
    domainEndSeconds,
    hasPower,
    hasHeartRate,
    hasCadence,
    hasElevation,
    hasGps,
    hasMinuteVentilation,
    hasBreathingFrequency,
    ftpWatts: toFiniteNumber(ftpWatts),
    overlays,
    overlaysByIntervalId
  };
}

export function createRideInteractionKey(ride: Ride | null): string | null {
  if (!ride) {
    return null;
  }

  const first = ride.records[0];
  const last = ride.records[ride.records.length - 1];
  return [
    ride.sourceFileName ?? "unknown",
    ride.sourceFileSizeBytes ?? "unknown",
    ride.records.length,
    first?.timestamp ?? first?.elapsedSeconds ?? "none",
    last?.timestamp ?? last?.elapsedSeconds ?? "none"
  ].join("|");
}

export function syncInteractionForRideChange(
  previous: RideAnalysisInteractionState,
  nextRideKey: string | null
): RideAnalysisInteractionState {
  if (previous.rideKey === nextRideKey) {
    return previous;
  }

  return {
    rideKey: nextRideKey,
    selectedIntervalId: null,
    viewportStartSeconds: null,
    viewportEndSeconds: null
  };
}

export function findIntervalIdAtElapsed(
  overlays: RideChartIntervalOverlay[],
  elapsedSeconds: number,
  includeRecovery = false
): string | null {
  for (const overlay of overlays) {
    if (overlay.kind === "recovery" && !includeRecovery) {
      continue;
    }

    if (elapsedSeconds >= overlay.startElapsedSeconds && elapsedSeconds <= overlay.endElapsedSeconds) {
      return overlay.intervalId;
    }
  }

  return null;
}

export function computeViewportForInterval(
  model: RideAnalysisChartModel,
  intervalId: string,
  paddingSeconds = 20,
  paddingFraction = 0.12
): { startSeconds: number; endSeconds: number } | null {
  const overlay = model.overlaysByIntervalId[intervalId];
  if (!overlay) {
    return null;
  }

  const intervalDuration = Math.max(1, overlay.endElapsedSeconds - overlay.startElapsedSeconds);
  const dynamicPadding = Math.max(paddingSeconds, intervalDuration * paddingFraction);

  const startSeconds = Math.max(model.domainStartSeconds, overlay.startElapsedSeconds - dynamicPadding);
  const endSeconds = Math.min(model.domainEndSeconds, overlay.endElapsedSeconds + dynamicPadding);

  if (endSeconds <= startSeconds) {
    return null;
  }

  return { startSeconds, endSeconds };
}

export function clampViewport(
  domainStartSeconds: number,
  domainEndSeconds: number,
  viewportStartSeconds: number,
  viewportEndSeconds: number,
  minWindowSeconds = 30
): { startSeconds: number; endSeconds: number } {
  const safeDomainStart = Number.isFinite(domainStartSeconds) ? domainStartSeconds : 0;
  const safeDomainEnd = Number.isFinite(domainEndSeconds) && domainEndSeconds > safeDomainStart ? domainEndSeconds : safeDomainStart + minWindowSeconds;
  const safeViewportStart = Number.isFinite(viewportStartSeconds) ? viewportStartSeconds : safeDomainStart;
  const safeViewportEnd = Number.isFinite(viewportEndSeconds) ? viewportEndSeconds : safeDomainEnd;

  const domainSpan = Math.max(minWindowSeconds, safeDomainEnd - safeDomainStart);
  const requestedSpan = Math.max(minWindowSeconds, safeViewportEnd - safeViewportStart);
  const span = Math.min(domainSpan, requestedSpan);

  let startSeconds = Math.max(safeDomainStart, safeViewportStart);
  let endSeconds = startSeconds + span;

  if (endSeconds > safeDomainEnd) {
    endSeconds = safeDomainEnd;
    startSeconds = endSeconds - span;
  }

  if (startSeconds < safeDomainStart) {
    startSeconds = safeDomainStart;
  }

  return { startSeconds, endSeconds };
}

export function findNearestPointIndex(points: RideChartPoint[], elapsedSeconds: number): number {
  if (points.length === 0) {
    return -1;
  }

  let low = 0;
  let high = points.length - 1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const value = points[mid].elapsedSeconds;

    if (value === elapsedSeconds) {
      return mid;
    }

    if (value < elapsedSeconds) {
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  const leftIndex = Math.max(0, high);
  const rightIndex = Math.min(points.length - 1, low);

  const leftDistance = Math.abs(points[leftIndex].elapsedSeconds - elapsedSeconds);
  const rightDistance = Math.abs(points[rightIndex].elapsedSeconds - elapsedSeconds);

  return leftDistance <= rightDistance ? leftIndex : rightIndex;
}
